import { Box, Container, Stack, Text } from "@mantine/core"
import { Button, Card, EqualWidthGrid, Loader, LogoDark } from "@tfg/ui"
import { createRef, useEffect } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { LoginBackgroundIllustrations } from "~/pages/Auth/components/LoginBackgroundIllustrations"
import { LoginTextInput } from "~/pages/Auth/components/LoginTextInput"
import {
  EXPIRED_MAGIC_LINK,
  ExpiredMagicLinkError,
} from "~/pages/Auth/errors/expired-magic-link.error"
import { useAuth } from "~/store/auth-context"

import { PasswordForm } from "./components/PasswordForm"
import { SuccessMessage } from "./components/SuccessMessage"
import { TwoFactorForm } from "./components/TwoFactorForm"
import { UsernameForm } from "./components/UsernameForm"
import { useLoginFlow } from "./hooks/useLoginFlow"

const publicKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

export const Login = () => {
  const { t } = useTranslation("account")
  const { isLoading, isAuthenticated, homeRedirectUrl } = useAuth()
  const [queryParams] = useSearchParams()
  const recaptchaRef = createRef<ReCAPTCHA>()
  const navigate = useNavigate()

  // Derive error state from query params
  const forwardedError =
    queryParams.get("error") === EXPIRED_MAGIC_LINK
      ? new ExpiredMagicLinkError()
      : null

  const {
    pageState,
    setPageState,
    email,
    userId,
    isLoggingIn,
    isSendingUsername,
    isSendingResetPasswordEmail,
    loginError,
    usernameError,
    handleUsernameSubmit,
    handleLogin,
    handleVerifyTwoFactor,
    handleForgotPassword,
  } = useLoginFlow()

  useEffect(() => {
    if (queryParams.get("redirected") === "supplier") {
      setPageState("username")
    }
  }, [queryParams, setPageState])

  useEffect(() => {
    if (isAuthenticated) {
      navigate(homeRedirectUrl)
    }
  }, [isAuthenticated, navigate, homeRedirectUrl])

  if (isLoading) return <Loader fullPage />

  const renderContent = () => {
    switch (pageState) {
      case "username":
        return (
          <UsernameForm
            onSubmit={handleUsernameSubmit}
            isLoading={isSendingUsername}
            error={forwardedError ?? usernameError}
          />
        )
      case "password":
        return (
          <PasswordForm
            onSubmit={handleLogin}
            onForgotPassword={() => setPageState("forgotPassword")}
            isLoading={isLoggingIn}
            error={loginError}
            username={email}
          />
        )
      case "twoFactor":
        return (
          <TwoFactorForm onSubmit={handleVerifyTwoFactor} userId={userId} />
        )
      case "forgotPassword":
        return (
          <>
            <Stack w="100%">
              <Text style={{ textAlign: "center" }}>
                {t("login.forgotPassword.title")}
              </Text>
              <LoginTextInput
                name="email"
                type="email"
                required
                autoFocus
                value={email}
                label={t("login.labels.email")}
                placeholder={t("login.placeholders.email")}
              />
            </Stack>
            <EqualWidthGrid>
              <Button
                size="sm"
                fullWidth={true}
                disabled={!email}
                loading={isSendingResetPasswordEmail}
                onClick={handleForgotPassword}
              >
                {t("login.forgotPassword.submit")}
              </Button>
              <Button
                size="sm"
                fullWidth={true}
                variant="secondary"
                disabled={isSendingResetPasswordEmail}
                onClick={() => setPageState("username")}
              >
                {t("login.forgotPassword.back")}
              </Button>
            </EqualWidthGrid>
          </>
        )
      case "resetPasswordEmailSent":
        return (
          <SuccessMessage
            message={t("login.forgotPassword.success")}
            onBack={() => setPageState("username")}
            isLoading={isSendingResetPasswordEmail}
          />
        )
      case "magicLinkEmailSent":
        return (
          <SuccessMessage
            message={t("login.magicLink.success")}
            // onBack={() => setPageState("username")}
            isLoading={isSendingResetPasswordEmail}
          />
        )
    }
  }

  return (
    <Container
      fluid
      bg="var(--mantine-color-gray-0)"
      w="100vw"
      h="100vh"
      pos="relative"
    >
      <Box pos="absolute" top={0} left={0} right={0} bottom={0}>
        <LoginBackgroundIllustrations />
      </Box>
      <Stack mb={100} align="center" justify="center" h="100vh">
        <LogoDark />
        <Card
          px="2rem"
          py="4rem"
          style={{ boxShadow: "none", border: "none" }}
          w={340}
        >
          {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
            <ReCAPTCHA
              sitekey={publicKey}
              size="invisible"
              ref={recaptchaRef}
            />
          )}
          <Card.Section align="center" px="lg">
            {renderContent()}
          </Card.Section>
          {import.meta.env.VITE_RECAPTCHA_ENABLED === "true" && (
            <Text style={{ textAlign: "center" }} mt="1rem">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
              apply.
            </Text>
          )}
        </Card>
      </Stack>
    </Container>
  )
}
